import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FaArrowRight, FaBell, FaBookOpen, FaNewspaper, FaSchool, FaUsers } from "react-icons/fa";
import adminApi from "services/admin-api";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { DataTable } from "components/molecules/table/table";
import { SchoolSelector } from "components/global/SchoolSelector";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import { useAuth } from "context/auth-context";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { navigateToSchoolWorkspace } from "utils/school-workspace-navigation";
import {
  buildSchoolActionQueueItems,
  SchoolActionQueue,
} from "components/admin/school/school-action-queue";
import { SchoolMatchWeekHighlights } from "components/admin/school/school-match-week-highlights";
import {
  DataTableSkeleton,
  EmptyState,
  PageHeaderSkeleton,
  PageHeader,
  SectionCard,
  StatCard,
  StatsGridSkeleton,
  StatusPill,
} from "components/admin";
import { formatDate, formatDateTime, formatMoney } from "utils/admin";
import { PortalAccessProfile } from "types/api";
import {
  canFulfillOwnedAthleteRequests,
  canManageOwnedAthleteClasses,
  canManageOwnedAthleteEvents,
  formatPortalLabel,
  getPortalTypeLabel,
  hasPortalCapability,
  isAthletePortalOwner,
} from "utils/portal-access";
import {
  DashboardSecondaryButton,
  DashboardSecondaryButtonSecondary,
  DashboardPrimaryButton,
  DashboardGridLayoutTertiary,
  DashboardSection,
  DashboardGridLayoutQuaternary,
  DashboardGridLayoutQuinary,
  DashboardGridLayoutSenary,
  DashboardGridLayoutSeptenary,
  DashboardGridLayoutOctonary,
  DashboardEmptyStatePanel,
  DashboardGridLayoutNonary,
  DashboardGridLayoutDenary,
  DashboardGridLayout,
  DashboardGridLayoutSecondary,
  DashboardGridLayoutGamma,
  DashboardInfoRow,
  DashboardContentStackTertiary,
  DashboardHeroImage,
  DashboardThumbnailImage,
  DashboardActionLink,
  DashboardActionButton,
  DashboardMetaTextSecondary,
  DashboardMetaTextTertiary,
  DashboardBodyText,
  DashboardEyebrowText,
  DashboardTitleText,
  DashboardLabelText,
  DashboardMetaText,
  DashboardIconBadge,
  DashboardIconBadgeSecondary,
  DashboardPulseSection,
  DashboardSchoolContextRow,
  DashboardSchoolContextActions,
  DashboardSchoolPrimaryButton,
  DashboardShortcutGrid,
  DashboardShortcutButton,
  DashboardShortcutMeta,
  DashboardFixtureList,
  DashboardFixtureRow,
  DashboardFixtureRowStatic,
  DashboardFixtureTitle,
  DashboardFixtureMeta,
} from "pages/app/dashboard/styles/dashboard.page.inline.styles";
const AdminDashboardContent: React.FC = () => {
  const history = useHistory();
  const { data: dashboard, isLoading } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: adminApi.dashboard,
  });
  const { data: athletes = [], isLoading: isAthletesLoading } = useQuery({
    queryKey: ["dashboard-athletes"],
    queryFn: () => adminApi.athletes.list(),
  });
  const { data: merchandise = [], isLoading: isMerchandiseLoading } = useQuery({
    queryKey: ["dashboard-merchandise"],
    queryFn: () =>
      adminApi.merchandise.list({
        sort: "popular",
      }),
  });
  const { data: newsFeed, isLoading: isNewsLoading } = useQuery({
    queryKey: ["dashboard-news"],
    queryFn: () =>
      adminApi.news.list({
        page: 1,
        per_page: 4,
      }),
  });
  const athleteLeaders = [...athletes]
    .sort((left: any, right: any) => (right.follower_count || 0) - (left.follower_count || 0))
    .slice(0, 5);
  const lowStockItems = [...merchandise]
    .sort((left: any, right: any) => (left.stock ?? 0) - (right.stock ?? 0))
    .slice(0, 5);
  const articles = newsFeed?.articles ?? [];
  return (
    <>
      <PageHeader
        title="Command Dashboard"
        description="Keep an eye on platform growth, marketplace health, and the latest athlete activity from one place."
        actions={
          <>
            <DashboardSecondaryButton onClick={() => history.push("/users")} type="button">
              Manage users
            </DashboardSecondaryButton>
            <DashboardPrimaryButton onClick={() => history.push("/sports")} type="button">
              Open sports
            </DashboardPrimaryButton>
          </>
        }
      />
      {isLoading ? (
        <StatsGridSkeleton />
      ) : (
        <DashboardGridLayout>
          <StatCard
            label="Users"
            value={dashboard?.overview?.users ?? "0"}
            helper={`${dashboard?.overview?.players ?? 0} players across the platform`}
          />
          <StatCard
            label="Competition"
            value={`${dashboard?.overview?.sports ?? 0} sports`}
            helper={`${dashboard?.overview?.teams ?? 0} teams seeded`}
          />
          <StatCard
            label="Marketplace"
            value={formatMoney(dashboard?.commerce?.completed_payment_volume ?? 0)}
            helper={`${dashboard?.commerce?.completed_payments ?? 0} completed payments`}
          />
          <StatCard
            label="Wallet Balance"
            value={formatMoney(dashboard?.wallets?.total_balance ?? 0)}
            helper={`${dashboard?.wallets?.total_wallets ?? 0} wallets in total`}
          />
        </DashboardGridLayout>
      )}

      <DashboardGridLayoutSecondary>
        <SectionCard
          action={
            <DashboardActionLink to="/athletes">
              Review athletes
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Follower momentum and roster visibility"
          title="Most Followed Athletes"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Athlete",
                accessor: (row: any) => (
                  <DashboardInfoRow>
                    <DashboardThumbnailImage
                      alt={row.full_name || "Athlete"}
                      fallbackLabel={row.full_name}
                      src={row.avatar_url || row.image}
                      variant="athlete"
                    />
                    <div>
                      <DashboardLabelText>{row.full_name}</DashboardLabelText>
                      <DashboardMetaText>{row.position || "Position pending"}</DashboardMetaText>
                    </div>
                  </DashboardInfoRow>
                ),
              },
              {
                label: "Team",
                accessor: (row: any) =>
                  row.primary_team?.name || row.team_names?.[0] || "Unassigned",
              },
              {
                label: "Followers",
                accessor: (row: any) => row.follower_count ?? 0,
              },
              {
                label: "Credits",
                accessor: (row: any) => row.in_app_credits ?? 0,
              },
            ]}
            data={athleteLeaders}
            emptyDescription="Athlete engagement data will appear here once records are available."
            emptyTitle="No athlete activity yet"
            hideNav
            isLoading={isAthletesLoading}
            loadingState={<DataTableSkeleton columns={4} rows={5} showThumbnail />}
            onView={(id) => history.push(`/athletes/${id}`)}
          />
        </SectionCard>

        <SectionCard subtitle="Operational health at a glance" title="Live Breakdown">
          <DashboardGridLayoutTertiary>
            <DashboardSection>
              <DashboardInfoRow>
                <DashboardIconBadge>
                  <FaSchool />
                </DashboardIconBadge>
                <div>
                  <DashboardLabelText>School operations</DashboardLabelText>
                  <DashboardMetaTextSecondary>
                    {dashboard?.overview?.countries ?? 0} countries mapped into the platform
                  </DashboardMetaTextSecondary>
                </div>
              </DashboardInfoRow>
            </DashboardSection>
            <DashboardSection>
              <DashboardInfoRow>
                <DashboardIconBadgeSecondary>
                  <FaUsers />
                </DashboardIconBadgeSecondary>
                <div>
                  <DashboardLabelText>Requests</DashboardLabelText>
                  <DashboardMetaTextSecondary>
                    {dashboard?.requests?.pending ?? 0} pending,{" "}
                    {dashboard?.requests?.fulfilled ?? 0} fulfilled
                  </DashboardMetaTextSecondary>
                </div>
              </DashboardInfoRow>
            </DashboardSection>
            <DashboardSection>
              <DashboardInfoRow>
                <DashboardIconBadgeSecondary>
                  <FaBookOpen />
                </DashboardIconBadgeSecondary>
                <div>
                  <DashboardLabelText>Classes</DashboardLabelText>
                  <DashboardMetaTextSecondary>
                    {dashboard?.commerce?.player_classes ?? 0} classes live in the catalog
                  </DashboardMetaTextSecondary>
                </div>
              </DashboardInfoRow>
            </DashboardSection>
            <DashboardSection>
              <DashboardInfoRow>
                <DashboardIconBadge>
                  <FaNewspaper />
                </DashboardIconBadge>
                <div>
                  <DashboardLabelText>News feed</DashboardLabelText>
                  <DashboardMetaTextSecondary>
                    {articles.length} recent stories ready to review
                  </DashboardMetaTextSecondary>
                </div>
              </DashboardInfoRow>
            </DashboardSection>
          </DashboardGridLayoutTertiary>
        </SectionCard>
      </DashboardGridLayoutSecondary>

      <DashboardGridLayoutQuaternary>
        <SectionCard
          action={
            <DashboardActionLink to="/merchandise">
              Open merchandise
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Stock risk and storefront readiness"
          title="Low Stock Merchandise"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Item",
                accessor: (row: any) => (
                  <DashboardInfoRow>
                    <DashboardThumbnailImage
                      alt={row.name || "Merchandise item"}
                      fallbackLabel={row.name}
                      src={row.image}
                      variant="merchandise"
                    />
                    <div>
                      <DashboardLabelText>{row.name}</DashboardLabelText>
                      <DashboardMetaText>
                        {row.team_name || row.player_name || "No linked team yet"}
                      </DashboardMetaText>
                    </div>
                  </DashboardInfoRow>
                ),
              },
              {
                label: "Player",
                accessor: (row: any) => row.player_name,
              },
              {
                label: "Stock",
                accessor: (row: any) => (
                  <StatusPill value={(row.stock ?? 0) > 3 ? "active" : "pending"} />
                ),
              },
              {
                label: "Price",
                accessor: (row: any) => row.formatted_price || formatMoney(row.price),
              },
            ]}
            data={lowStockItems}
            emptyDescription="Inventory alerts will appear here when merchandise records exist."
            emptyTitle="No merchandise data"
            hideNav
            isLoading={isMerchandiseLoading}
            loadingState={<DataTableSkeleton columns={4} rows={5} showThumbnail />}
            onView={(id) => history.push(`/merchandise/${id}`)}
          />
        </SectionCard>

        <SectionCard
          action={
            <DashboardActionLink to="/news">
              Open news feed
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Latest articles currently available in the API"
          title="News Desk"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Story",
                accessor: (row: any) => (
                  <div>
                    <DashboardLabelText>{row.title}</DashboardLabelText>
                    <DashboardMetaTextTertiary>
                      {row.excerpt || "No excerpt is available for this story yet."}
                    </DashboardMetaTextTertiary>
                  </div>
                ),
              },
              {
                label: "Sport",
                accessor: (row: any) => row.sport_name || "General",
              },
              {
                label: "Published",
                accessor: (row: any) => formatDate(row.published_at),
              },
            ]}
            data={articles}
            emptyDescription="Once news articles are seeded or synced, they will surface here."
            emptyTitle="No news articles"
            hideNav
            isLoading={isNewsLoading}
            loadingState={
              <DataTableSkeleton columns={3} rows={4} showThumbnail={false} showActions={false} />
            }
            onView={(id) => history.push(`/news/${id}`)}
          />
        </SectionCard>
      </DashboardGridLayoutQuaternary>
    </>
  );
};
const FanDashboardContent: React.FC<{
  currentUser: any;
}> = ({ currentUser }) => {
  const history = useHistory();
  const followedQuery = useQuery({
    queryKey: ["dashboard-followed-athletes"],
    queryFn: () => adminApi.users.followedAthletes(),
  });
  const walletQuery = useQuery({
    queryKey: ["dashboard-wallet"],
    queryFn: adminApi.wallets.me,
  });
  const ordersQuery = useQuery({
    queryKey: ["dashboard-orders"],
    queryFn: () =>
      adminApi.orders.list({
        page: 1,
        per_page: 5,
      }),
  });
  const notificationsQuery = useQuery({
    queryKey: ["dashboard-notifications"],
    queryFn: () =>
      adminApi.notifications.list({
        page: 1,
        per_page: 10,
      }),
  });
  const newsQuery = useQuery({
    queryKey: ["dashboard-fan-news"],
    queryFn: () =>
      adminApi.news.list({
        page: 1,
        per_page: 4,
      }),
  });
  const followedAthletes = followedQuery.data || [];
  const orders = ordersQuery.data || [];
  const notifications = notificationsQuery.data?.notifications || [];
  const unreadCount =
    notificationsQuery.data?.meta?.unread_count ??
    notifications.filter((item: any) => !item.read_at).length;
  const articles = newsQuery.data?.articles || [];
  return (
    <>
      <PageHeader
        title="Fan Dashboard"
        description="Track your followed athletes, recent purchases, wallet balance, and the latest stories from one role-specific home."
        actions={
          <>
            <DashboardSecondaryButton
              onClick={() => history.push("/workspace/followed-athletes")}
              type="button"
            >
              Manage follows
            </DashboardSecondaryButton>
            <DashboardPrimaryButton onClick={() => history.push("/workspace/wallet")} type="button">
              Open wallet
            </DashboardPrimaryButton>
          </>
        }
      />

      <DashboardGridLayout>
        <StatCard label="Followed Athletes" value={followedAthletes.length} />
        <StatCard
          label="Wallet Balance"
          value={walletQuery.data?.formatted_balance || formatMoney(walletQuery.data?.balance)}
        />
        <StatCard label="Orders" value={orders.length} />
        <StatCard label="Unread Notifications" value={unreadCount} />
      </DashboardGridLayout>

      <DashboardGridLayoutQuinary>
        <SectionCard
          action={
            <DashboardActionLink to="/workspace/followed-athletes">
              Open follows
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Athletes you currently follow"
          title="My Athletes"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Athlete",
                accessor: (row: any) => (
                  <DashboardInfoRow>
                    <DashboardThumbnailImage
                      alt={row.full_name || "Athlete"}
                      fallbackLabel={row.full_name}
                      src={row.avatar_url || row.image}
                      variant="athlete"
                    />
                    <div>
                      <DashboardLabelText>{row.full_name}</DashboardLabelText>
                      <DashboardMetaText>
                        {row.primary_team?.name || row.team_names?.[0] || "No team linked"}
                      </DashboardMetaText>
                    </div>
                  </DashboardInfoRow>
                ),
              },
              {
                label: "Sport",
                accessor: (row: any) => row.sport_names?.[0] || "Unknown",
              },
              {
                label: "Followers",
                accessor: (row: any) => row.follower_count ?? 0,
              },
            ]}
            data={followedAthletes}
            emptyDescription="Athletes you follow will appear here."
            emptyTitle="No followed athletes yet"
            hideNav
            isLoading={followedQuery.isLoading}
            loadingState={<DataTableSkeleton columns={3} rows={4} showThumbnail />}
            onView={(id) => history.push(`/athletes/${id}`)}
          />
        </SectionCard>

        <SectionCard
          action={
            <DashboardActionLink to="/workspace/orders">
              Open orders
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Recent storefront activity"
          title="My Orders"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Item",
                accessor: (row: any) => row.merchandise_item?.name || "Unknown item",
              },
              {
                label: "Total",
                accessor: (row: any) => row.formatted_total_price || formatMoney(row.total_price),
              },
              {
                label: "Status",
                accessor: (row: any) => <StatusPill value={row.status} />,
              },
              {
                label: "Placed",
                accessor: (row: any) => formatDateTime(row.created_at),
              },
            ]}
            data={orders}
            emptyDescription="Order history will appear here after your first purchase."
            emptyTitle="No orders yet"
            hideNav
            isLoading={ordersQuery.isLoading}
            loadingState={<DataTableSkeleton columns={4} rows={4} />}
          />
        </SectionCard>
      </DashboardGridLayoutQuinary>

      <DashboardGridLayoutQuinary>
        <SectionCard
          action={
            <DashboardActionLink to="/news">
              Open news
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Recent stories available to your account"
          title="News Feed"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Story",
                accessor: (row: any) => (
                  <div>
                    <DashboardLabelText>{row.title}</DashboardLabelText>
                    <DashboardMetaTextTertiary>
                      {row.excerpt || "No excerpt available."}
                    </DashboardMetaTextTertiary>
                  </div>
                ),
              },
              {
                label: "Sport",
                accessor: (row: any) => row.sport_name || "General",
              },
              {
                label: "Published",
                accessor: (row: any) => formatDate(row.published_at),
              },
            ]}
            data={articles}
            emptyDescription="News will appear here when stories are available."
            emptyTitle="No news stories"
            hideNav
            isLoading={newsQuery.isLoading}
            loadingState={<DataTableSkeleton columns={3} rows={4} />}
            onView={(id) => history.push(`/news/${id}`)}
          />
        </SectionCard>

        <SectionCard subtitle="Inbox activity" title="Notifications">
          <DashboardGridLayoutSenary>
            <DashboardSection>
              <DashboardInfoRow>
                <DashboardIconBadgeSecondary>
                  <FaBell />
                </DashboardIconBadgeSecondary>
                <div>
                  <DashboardLabelText>{unreadCount} unread</DashboardLabelText>
                  <DashboardMetaTextSecondary>
                    {notifications.length} notifications in your latest feed
                  </DashboardMetaTextSecondary>
                </div>
              </DashboardInfoRow>
            </DashboardSection>
            <DashboardSecondaryButtonSecondary
              onClick={() => history.push("/workspace/notifications")}
              type="button"
            >
              Open notifications
            </DashboardSecondaryButtonSecondary>
            <DashboardBodyText>
              Signed in as {currentUser?.full_name || currentUser?.email || "Athspire user"} in the{" "}
              {getPortalTypeLabel(currentUser?.portal_access, currentUser?.role).toLowerCase()}.
            </DashboardBodyText>
          </DashboardGridLayoutSenary>
        </SectionCard>
      </DashboardGridLayoutQuinary>
    </>
  );
};
const AthleteDashboardContent: React.FC<{
  currentUser: any;
}> = ({ currentUser }) => {
  const history = useHistory();
  const playerId = currentUser?.player?.id;
  const portalAccess = currentUser?.portal_access;
  const athleteOwnedAccount = isAthletePortalOwner(currentUser, portalAccess);
  const canManageEvents = canManageOwnedAthleteEvents(currentUser, portalAccess);
  const canManageClasses = canManageOwnedAthleteClasses(currentUser, portalAccess);
  const canFulfillRequests = canFulfillOwnedAthleteRequests(currentUser, portalAccess);
  const visibleSections = portalAccess?.visible_sections || [];
  const athleteQuery = useQuery({
    queryKey: ["dashboard-athlete-profile", playerId],
    queryFn: () => adminApi.athletes.get(playerId),
    enabled: Boolean(playerId),
  });
  const classesQuery = useQuery({
    queryKey: ["dashboard-athlete-classes", playerId],
    queryFn: () =>
      adminApi.playerClasses.list({
        player_id: playerId,
        per_page: 5,
      }),
    enabled: Boolean(playerId),
  });
  const eventsQuery = useQuery({
    queryKey: ["dashboard-athlete-events", playerId],
    queryFn: () =>
      adminApi.events.list({
        player_id: playerId,
        page: 1,
        per_page: 5,
      }),
    enabled: Boolean(playerId),
  });
  const requestsQuery = useQuery({
    queryKey: ["dashboard-athlete-requests", playerId],
    queryFn: () =>
      adminApi.requests.list(playerId, {
        page: 1,
        per_page: 5,
      }),
    enabled: Boolean(playerId),
  });
  const recordsQuery = useQuery({
    queryKey: ["dashboard-athlete-records", playerId],
    queryFn: () => adminApi.playerPerformanceRecords.list(playerId),
    enabled: Boolean(playerId),
  });
  const walletQuery = useQuery({
    queryKey: ["dashboard-athlete-wallet"],
    queryFn: adminApi.wallets.me,
  });
  if (!playerId) {
    return (
      <>
        <PageHeader
          title="Athlete Dashboard"
          description="This dashboard becomes available when the current account has a linked athlete record."
        />
        <EmptyState
          description="The API has not linked this account to a player record yet."
          title="No athlete profile available"
        />
      </>
    );
  }
  const athlete = athleteQuery.data;
  const events = eventsQuery.data || [];
  const classes = classesQuery.data || [];
  const requests = requestsQuery.data || [];
  const records = recordsQuery.data || [];
  const actionButtons = [
    visibleSections.includes("player_profile")
      ? {
          label: "My profile",
          path: "/workspace/player-profile",
          tone: "secondary" as const,
        }
      : null,
    visibleSections.includes("player_stats")
      ? {
          label: "My stats",
          path: "/workspace/player-stats",
          tone: "primary" as const,
        }
      : null,
    visibleSections.includes("player_performance_records")
      ? {
          label: "Performance",
          path: "/workspace/player-performance-records",
          tone: "secondary" as const,
        }
      : null,
  ].filter(Boolean) as Array<{
    label: string;
    path: string;
    tone: "primary" | "secondary";
  }>;
  return (
    <>
      <PageHeader
        title="Athlete Dashboard"
        description="See the player-facing tools tied to your athlete account, including your profile, training content, events, performance history, and request fulfilment controls."
        actions={
          <>
            {actionButtons.map((action) => (
              <button
                className={
                  action.tone === "primary"
                    ? "rounded-2xl bg-[var(--brand-accent)] px-4 py-3 text-sm font-medium text-white transition hover:bg-[var(--brand-accent-deep)]"
                    : "rounded-2xl border border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] px-4 py-3 text-sm font-medium text-[var(--brand-ink)] transition hover:border-[var(--brand-highlight)] hover:bg-[rgba(240,191,86,0.08)]"
                }
                key={action.path}
                onClick={() => history.push(action.path)}
                type="button"
              >
                {action.label}
              </button>
            ))}
          </>
        }
      />

      <DashboardGridLayout>
        <StatCard
          label="Athlete"
          value={athlete?.full_name || currentUser?.player?.full_name || "Unknown"}
        />
        <StatCard
          label="Wallet Balance"
          value={walletQuery.data?.formatted_balance || formatMoney(walletQuery.data?.balance)}
        />
        <StatCard label="Events" value={events.length} />
        <StatCard label="Classes" value={classes.length} />
      </DashboardGridLayout>

      <DashboardGridLayoutSeptenary>
        <SectionCard
          subtitle="Current athlete identity returned by the API"
          title="Profile Snapshot"
        >
          <DashboardGridLayoutOctonary>
            <DashboardHeroImage
              alt={athlete?.full_name || "Athlete"}
              fallbackLabel={athlete?.full_name}
              src={athlete?.avatar_url || athlete?.image}
              variant="athlete"
            />
            <DashboardGridLayoutTertiary>
              {[
                ["Position", athlete?.position || currentUser?.player?.position || "Unknown"],
                [
                  "Primary team",
                  athlete?.primary_team?.name || athlete?.team_names?.[0] || "Unknown",
                ],
                ["Sports", (athlete?.sport_names || []).join(", ") || "Unknown"],
                ["Credits", athlete?.in_app_credits ?? 0],
              ].map(([label, value]) => (
                <DashboardSection key={String(label)}>
                  <DashboardEyebrowText>{label}</DashboardEyebrowText>
                  <DashboardTitleText>{value}</DashboardTitleText>
                </DashboardSection>
              ))}
            </DashboardGridLayoutTertiary>
          </DashboardGridLayoutOctonary>
        </SectionCard>

        <SectionCard subtitle="The tools available to this player account" title="My Workspace">
          <DashboardGridLayoutTertiary>
            <DashboardSection>
              <DashboardEyebrowText>Profile</DashboardEyebrowText>
              <DashboardTitleText>
                {hasPortalCapability(portalAccess, "can_manage_own_player_profile")
                  ? "Editable"
                  : "Read only"}
              </DashboardTitleText>
            </DashboardSection>
            <DashboardSection>
              <DashboardEyebrowText>Performance</DashboardEyebrowText>
              <DashboardTitleText>{records.length} records available</DashboardTitleText>
            </DashboardSection>
            <DashboardSection>
              <DashboardEyebrowText>Events</DashboardEyebrowText>
              <DashboardTitleText>
                {canManageEvents ? "Manage my events" : "Read only"}
              </DashboardTitleText>
            </DashboardSection>
            <DashboardSection>
              <DashboardEyebrowText>Classes</DashboardEyebrowText>
              <DashboardTitleText>
                {canManageClasses ? "Manage my classes" : "Read only"}
              </DashboardTitleText>
            </DashboardSection>
          </DashboardGridLayoutTertiary>
          <DashboardEmptyStatePanel>
            {athleteOwnedAccount
              ? "This athlete login is scoped to your own player profile. You can update your own profile, maintain your events and classes, manage your performance records, and fulfil request IDs that belong to your athlete account."
              : "This account is linked to athlete tools, but ownership controls may be limited by the current portal profile."}
          </DashboardEmptyStatePanel>
        </SectionCard>
      </DashboardGridLayoutSeptenary>

      <DashboardGridLayoutNonary>
        <SectionCard
          action={
            <DashboardActionLink to="/workspace/player-performance-records">
              Open performance
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Cross-source records currently tied to your athlete profile"
          title="Performance Records"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Source",
                accessor: (row: any) => row.source_name || "Unknown",
              },
              {
                label: "Type",
                accessor: (row: any) => (
                  <StatusPill value={row.source_type || row.record_type || "event"} />
                ),
              },
              {
                label: "Season",
                accessor: (row: any) => row.season || "Unknown",
              },
              {
                label: "Recorded",
                accessor: (row: any) => formatDate(row.recorded_on),
              },
            ]}
            data={records.slice(0, 5)}
            emptyDescription="Performance records linked to your athlete profile will appear here."
            emptyTitle="No performance records yet"
            hideNav
            isLoading={recordsQuery.isLoading}
            loadingState={<DataTableSkeleton columns={4} rows={4} />}
          />
        </SectionCard>

        <SectionCard
          action={
            <DashboardActionLink to="/workspace/events">
              Open events
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Your latest calendar items"
          title="Events"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Event",
                accessor: (row: any) => row.name || "Untitled event",
              },
              {
                label: "Date",
                accessor: (row: any) => formatDate(row.date),
              },
              {
                label: "Location",
                accessor: (row: any) => row.location || "TBC",
              },
              {
                label: "Details",
                accessor: (row: any) => row.details || "No details",
              },
            ]}
            data={events}
            emptyDescription={
              canManageEvents
                ? "Events you create will appear here."
                : "No events are available for this athlete yet."
            }
            emptyTitle="No events yet"
            hideNav
            isLoading={eventsQuery.isLoading}
            loadingState={<DataTableSkeleton columns={4} rows={4} />}
          />
        </SectionCard>
      </DashboardGridLayoutNonary>

      <DashboardGridLayoutDenary>
        <SectionCard
          action={
            <DashboardActionLink to="/workspace/player-classes">
              Open classes
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="Training content tied to your athlete account"
          title="Player Classes"
        >
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Class",
                accessor: (row: any) => row.title || "Untitled class",
              },
              {
                label: "Price",
                accessor: (row: any) => row.formatted_price || formatMoney(row.price),
              },
              {
                label: "Created",
                accessor: (row: any) => formatDateTime(row.created_at),
              },
            ]}
            data={classes}
            emptyDescription={
              canManageClasses
                ? "Classes you create will appear here."
                : "No player classes are linked to this athlete yet."
            }
            emptyTitle="No classes yet"
            hideNav
            isLoading={classesQuery.isLoading}
            loadingState={<DataTableSkeleton columns={3} rows={4} />}
          />
        </SectionCard>

        <SectionCard
          action={
            <DashboardActionLink to="/workspace/requests">
              Open requests
              <FaArrowRight size={12} />
            </DashboardActionLink>
          }
          subtitle="What the current request API exposes for athlete logins"
          title="Request Fulfilment"
        >
          <DashboardGridLayoutSenary>
            <DashboardSection>
              <DashboardEyebrowText>Fulfilment</DashboardEyebrowText>
              <DashboardTitleText>
                {canFulfillRequests ? "Available by request ID" : "Not available"}
              </DashboardTitleText>
            </DashboardSection>
            <DashboardSection>
              <DashboardEyebrowText>Incoming inbox</DashboardEyebrowText>
              <DashboardTitleText>
                {requests.length > 0 ? `${requests.length} visible` : "Pending backend support"}
              </DashboardTitleText>
            </DashboardSection>
            <DashboardBodyText>
              Athlete-owned accounts can fulfil their own requests when they already have the
              request UUID. The current API still does not return a dedicated incoming request inbox
              for player logins, so the requests workspace stays focused on fulfilment tools for
              now.
            </DashboardBodyText>
          </DashboardGridLayoutSenary>
        </SectionCard>
      </DashboardGridLayoutDenary>
    </>
  );
};
const SchoolDashboardContent: React.FC<{
  portalAccess?: PortalAccessProfile | null;
}> = ({ portalAccess }) => {
  const history = useHistory();
  const { setActiveSchoolId } = useAuth();
  const { schoolId, schools, schoolsQuery } = useScopedSchoolSelection({
    manageActiveSchoolId: true,
    portalAccess,
    queryKey: ["workspace-schools"],
  });
  const openSchoolWorkspace = useCallback(
    (path: string, search?: string) => {
      navigateToSchoolWorkspace(history, setActiveSchoolId, {
        path,
        schoolId,
        state: {
          backTo: "/dashboard",
        },
        search,
      });
    },
    [history, schoolId, setActiveSchoolId],
  );
  const visibleSections = portalAccess?.visible_sections || [];
  const schoolDashboardQuery = useQuery({
    queryKey: ["dashboard-school-overview", schoolId],
    queryFn: () => adminApi.schoolDashboards.get(schoolId),
    enabled: Boolean(schoolId),
  });
  const schoolDashboard = schoolDashboardQuery.data;
  const rosterTasks = useMemo(
    () => schoolDashboard?.roster_tasks || [],
    [schoolDashboard?.roster_tasks],
  );
  const upcomingMatches = useMemo(
    () => schoolDashboard?.upcoming_matches || [],
    [schoolDashboard?.upcoming_matches],
  );
  const canOpenSchools = visibleSections.includes("schools");
  const canOpenSchedule =
    visibleSections.includes("school_calendar") ||
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_scoresheets") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_dashboard");
  const canOpenTeams = visibleSections.includes("school_teams");
  const canOpenPlayers = visibleSections.includes("school_players");
  const canOpenStaff = visibleSections.includes("school_roles");
  const canOpenProgrammes = visibleSections.includes("school_sports");
  const canOpenPerformance = visibleSections.includes("player_performance_records");
  const canOpenCalendar =
    visibleSections.includes("school_calendar") ||
    visibleSections.includes("school_dashboard") ||
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_scoresheets");
  const canOpenFixtures =
    visibleSections.includes("school_matches") ||
    visibleSections.includes("school_dashboard") ||
    visibleSections.includes("school_rosters") ||
    visibleSections.includes("school_scoresheets");
  const actionQueueItems = useMemo(
    () =>
      buildSchoolActionQueueItems({
        onOpenMatchHub: (matchId) =>
          openSchoolWorkspace(`/workspace/school-matches/${matchId}`),
        onOpenSchedule: (search) => openSchoolWorkspace("/workspace/schedule", search),
        onOpenSchoolDocuments: () => openSchoolWorkspace("/workspace/school", "?tab=documents"),
        onOpenScoresheet: (matchId) =>
          openSchoolWorkspace(`/workspace/school-scoresheets/${matchId}`),
        schoolDashboard,
      }),
    [openSchoolWorkspace, schoolDashboard],
  );
  const isStatsLoading =
    schoolsQuery.isLoading || (Boolean(schoolId) && schoolDashboardQuery.isLoading);
  const documentTotal = Number(schoolDashboard?.document_summary?.total_count ?? 0);
  const documentVerified = Number(schoolDashboard?.document_summary?.verified_count ?? 0);
  const documentsNeedingReview = Math.max(documentTotal - documentVerified, 0);
  const galleryCount = (schoolDashboard?.gallery_preview || []).length;
  const previewFixtures = useMemo(
    () => (upcomingMatches || []).slice(0, 8),
    [upcomingMatches],
  );
  const workspaceShortcuts = useMemo(() => {
    const shortcuts: Array<{
      id: string;
      label: string;
      meta?: string;
      onClick: () => void;
    }> = [];

    if (canOpenSchools) {
      shortcuts.push({
        id: "school-overview",
        label: "School overview",
        meta: "Hub home",
        onClick: () => openSchoolWorkspace("/workspace/school"),
      });
    }

    if (canOpenStaff) {
      shortcuts.push({
        id: "staff",
        label: "Staff",
        meta: "Roles & org chart",
        onClick: () => openSchoolWorkspace("/workspace/school", "?tab=staff"),
      });
    }

    if (canOpenProgrammes) {
      shortcuts.push({
        id: "programmes",
        label: "Programmes",
        meta: `${schoolDashboard?.overview?.visible_sports_count ?? 0} sports`,
        onClick: () => openSchoolWorkspace("/workspace/school", "?tab=programmes"),
      });
    }

    if (canOpenSchools) {
      shortcuts.push({
        id: "media",
        label: "Media gallery",
        meta: galleryCount > 0 ? `${galleryCount} preview${galleryCount === 1 ? "" : "s"}` : "Open gallery",
        onClick: () => openSchoolWorkspace("/workspace/school", "?tab=media"),
      });
      shortcuts.push({
        id: "documents",
        label: "Documents",
        meta:
          documentsNeedingReview > 0
            ? `${documentsNeedingReview} need review`
            : documentTotal > 0
            ? `${documentVerified} verified`
            : "Open vault",
        onClick: () => openSchoolWorkspace("/workspace/school", "?tab=documents"),
      });
    }

    if (canOpenTeams) {
      shortcuts.push({
        id: "teams",
        label: "Teams",
        meta: `${schoolDashboard?.overview?.visible_teams_count ?? 0} in view`,
        onClick: () => openSchoolWorkspace("/workspace/school-teams"),
      });
    }

    if (canOpenPlayers) {
      shortcuts.push({
        id: "people",
        label: "People",
        meta: `${schoolDashboard?.overview?.visible_players_count ?? 0} players`,
        onClick: () => openSchoolWorkspace("/workspace/school-players"),
      });
    }

    if (canOpenCalendar) {
      shortcuts.push({
        id: "calendar",
        label: "Calendar",
        meta: "Schedule hub",
        onClick: () => openSchoolWorkspace("/workspace/schedule", "?view=calendar"),
      });
    }

    if (canOpenFixtures) {
      shortcuts.push({
        id: "fixtures",
        label: "Fixtures",
        meta: `${upcomingMatches.length} upcoming`,
        onClick: () => openSchoolWorkspace("/workspace/schedule", "?view=list&focus=fixtures"),
      });
    }

    if (canOpenPerformance) {
      shortcuts.push({
        id: "performance",
        label: "Performance",
        meta: "Records & highlights",
        onClick: () => openSchoolWorkspace("/workspace/player-performance-records"),
      });
    }

    return shortcuts;
  }, [
    canOpenCalendar,
    canOpenFixtures,
    canOpenPerformance,
    canOpenPlayers,
    canOpenProgrammes,
    canOpenSchools,
    canOpenStaff,
    canOpenTeams,
    documentTotal,
    documentVerified,
    documentsNeedingReview,
    galleryCount,
    openSchoolWorkspace,
    schoolDashboard?.overview?.visible_players_count,
    schoolDashboard?.overview?.visible_sports_count,
    schoolDashboard?.overview?.visible_teams_count,
    upcomingMatches.length,
  ]);

  return (
    <>
      <DashboardSchoolContextRow>
        <SchoolSelector portalAccess={portalAccess ?? null} />
        <DashboardSchoolContextActions>
          {canOpenSchedule ? (
            <DashboardSecondaryButton
              onClick={() => openSchoolWorkspace("/workspace/schedule")}
              type="button"
            >
              Open schedule
            </DashboardSecondaryButton>
          ) : null}
          {canOpenSchools ? (
            <DashboardSchoolPrimaryButton
              onClick={() => openSchoolWorkspace("/workspace/school")}
              type="button"
            >
              Open school
            </DashboardSchoolPrimaryButton>
          ) : null}
        </DashboardSchoolContextActions>
      </DashboardSchoolContextRow>

      {!schoolsQuery.isLoading && schools.length === 0 ? (
        <EmptyState
          description="This account is not linked to any schools yet. Once school access is assigned, KPIs and actions will appear here."
          title="No schools in scope"
        />
      ) : (
        <>
          {isStatsLoading ? (
            <StatsGridSkeleton />
          ) : (
            <DashboardGridLayout>
              <StatCard
                helper={
                  schools.length > 1 ? `${schools.length} schools in your scope` : undefined
                }
                label="Sports"
                value={schoolDashboard?.overview?.visible_sports_count ?? 0}
              />
              <StatCard
                label="Teams"
                value={schoolDashboard?.overview?.visible_teams_count ?? 0}
              />
              <StatCard
                label="Players"
                value={schoolDashboard?.overview?.visible_players_count ?? 0}
              />
              <StatCard
                helper={`${schoolDashboard?.overview?.submitted_rosters_count ?? 0} submitted rosters`}
                label="Roster Tasks"
                value={rosterTasks.length}
              />
            </DashboardGridLayout>
          )}

          {schoolId ? (
            actionQueueItems.length > 0 ? (
              <DashboardPulseSection>
                <SchoolActionQueue items={actionQueueItems} />
              </DashboardPulseSection>
            ) : (
              <DashboardPulseSection>
                <SectionCard
                  subtitle="Nothing urgent for this school right now."
                  title="Needs attention"
                >
                  <DashboardBodyText>
                    Rosters, scoresheets, and document reviews will show up here when they need
                    action.
                  </DashboardBodyText>
                </SectionCard>
              </DashboardPulseSection>
            )
          ) : null}

          {schoolId ? (
            <DashboardGridLayoutQuinary>
              <SchoolMatchWeekHighlights
                isLoading={schoolDashboardQuery.isLoading}
                matchWeek={schoolDashboard?.match_week}
                onSelectMatch={(matchId) =>
                  openSchoolWorkspace(`/workspace/school-matches/${matchId}`)
                }
                onSelectPlayer={(playerId) =>
                  openSchoolWorkspace(`/workspace/school-players/${schoolId}/${playerId}`)
                }
                onSelectTeam={(teamId) =>
                  openSchoolWorkspace(`/workspace/school-teams/${schoolId}/${teamId}`)
                }
                subtitle="Weekly highlights for the active school."
                weeklyHighlights={schoolDashboard?.weekly_highlights}
              />

              <SectionCard
                action={
                  canOpenFixtures ? (
                    <DashboardActionButton
                      onClick={() =>
                        openSchoolWorkspace("/workspace/schedule", "?view=list&focus=fixtures")
                      }
                      type="button"
                    >
                      View all fixtures
                      <FaArrowRight size={12} />
                    </DashboardActionButton>
                  ) : undefined
                }
                subtitle="Next fixtures for the active school"
                title="Upcoming Matches"
              >
                {schoolDashboardQuery.isLoading ? (
                  <DashboardFixtureList aria-label="Loading upcoming matches" role="status">
                    {[0, 1, 2].map((index) => (
                      <DashboardFixtureRowStatic key={index}>
                        <DashboardFixtureTitle>Loading fixture…</DashboardFixtureTitle>
                        <DashboardFixtureMeta>Please wait</DashboardFixtureMeta>
                      </DashboardFixtureRowStatic>
                    ))}
                  </DashboardFixtureList>
                ) : previewFixtures.length === 0 ? (
                  <EmptyState
                    description="Upcoming fixtures will appear here for the selected school."
                    title="No upcoming fixtures"
                  />
                ) : (
                  <DashboardFixtureList>
                    {previewFixtures.map((match: any) => {
                      const matchId = match?.id ? String(match.id) : "";
                      const homeName =
                        match.home_school_team?.name || match.home_team_name || "Home";
                      const awayName =
                        match.away_school_team?.name || match.away_team_name || "Away";
                      const whenLabel = match.scheduled_at
                        ? formatDateTime(match.scheduled_at)
                        : "Date pending";
                      const venueLabel = match.venue_name || match.venue_address;
                      const sportLabel = match.sport_name;
                      const openMatch = () => {
                        if (matchId) {
                          openSchoolWorkspace(`/workspace/school-matches/${matchId}`);
                          return;
                        }
                        if (canOpenFixtures) {
                          openSchoolWorkspace(
                            "/workspace/schedule",
                            "?view=list&focus=fixtures",
                          );
                        }
                      };

                      return (
                        <DashboardFixtureRow
                          key={matchId || `${homeName}-${awayName}-${match.scheduled_at}`}
                          onClick={openMatch}
                          type="button"
                        >
                          <DashboardFixtureTitle>
                            {homeName} vs {awayName}
                          </DashboardFixtureTitle>
                          <DashboardFixtureMeta>
                            {[whenLabel, sportLabel, venueLabel].filter(Boolean).join(" · ")}
                          </DashboardFixtureMeta>
                        </DashboardFixtureRow>
                      );
                    })}
                  </DashboardFixtureList>
                )}
              </SectionCard>
            </DashboardGridLayoutQuinary>
          ) : null}

          {schoolId && workspaceShortcuts.length > 0 ? (
            <DashboardPulseSection>
              <SectionCard
                subtitle="Jump straight into the right school workspace area for the active school."
                title="Quick access"
              >
                <DashboardShortcutGrid>
                  {workspaceShortcuts.map((shortcut) => (
                    <DashboardShortcutButton
                      key={shortcut.id}
                      onClick={shortcut.onClick}
                      type="button"
                    >
                      <span>{shortcut.label}</span>
                      {shortcut.meta ? (
                        <DashboardShortcutMeta>{shortcut.meta}</DashboardShortcutMeta>
                      ) : (
                        <FaArrowRight size={12} />
                      )}
                    </DashboardShortcutButton>
                  ))}
                </DashboardShortcutGrid>
              </SectionCard>
            </DashboardPulseSection>
          ) : null}
        </>
      )}
    </>
  );
};
const WorkspaceFallbackContent: React.FC<{
  currentUser: any;
}> = ({ currentUser }) => (
  <>
    <PageHeader
      title="Dashboard"
      description="Your workspace is ready, but this account does not currently expose a dedicated dashboard profile."
    />
    <SectionCard subtitle="Current account" title="Workspace Summary">
      <DashboardGridLayoutGamma>
        {[
          ["Name", currentUser?.full_name || currentUser?.name || "Unknown"],
          ["Email", currentUser?.email || "Unknown"],
          ["Role", formatPortalLabel(currentUser?.role)],
          ["Workspace", getPortalTypeLabel(currentUser?.portal_access, currentUser?.role)],
        ].map(([label, value]) => (
          <DashboardSection key={String(label)}>
            <DashboardEyebrowText>{label}</DashboardEyebrowText>
            <DashboardTitleText>{value}</DashboardTitleText>
          </DashboardSection>
        ))}
      </DashboardGridLayoutGamma>
    </SectionCard>
  </>
);
const DashboardPage = () => {
  const { data: currentUser, isLoading } = useQuery(buildCurrentUserQueryOptions());
  const portalAccess = currentUser?.portal_access;
  let content: React.ReactNode = <WorkspaceFallbackContent currentUser={currentUser} />;
  if (
    portalAccess?.portal_type === "admin" &&
    hasPortalCapability(portalAccess, "can_view_admin_dashboard")
  ) {
    content = <AdminDashboardContent />;
  } else if (portalAccess?.portal_type === "athlete") {
    content = <AthleteDashboardContent currentUser={currentUser} />;
  } else if (portalAccess?.portal_type === "school_management") {
    content = <SchoolDashboardContent portalAccess={portalAccess} />;
  } else if (portalAccess?.portal_type === "fan") {
    content = <FanDashboardContent currentUser={currentUser} />;
  }
  return (
    <AppLayout route="/dashboard">
      {isLoading ? (
        <DashboardContentStackTertiary>
          <PageHeaderSkeleton />
          <StatsGridSkeleton />
          <DataTableSkeleton columns={4} rows={5} />
        </DashboardContentStackTertiary>
      ) : (
        content
      )}
    </AppLayout>
  );
};
export default DashboardPage;

